// Shared "sign in and I'll carry on" affordance (v1.2).
//
// Before this, a dead refresh token surfaced as the raw NotAuthedError string
// ("Product list unavailable — no delegated token available — sign in while
// online") in the middle of a report, which told a buyer on the floor nothing
// actionable. Now the same condition renders one button that signs them back in
// and immediately re-runs whatever they were trying to do.
//
// WHY THE RETRY LIVES IN A TAP HANDLER: iOS Safari only allows a popup that opens
// inside the user's own gesture. `signIn()` calls loginPopup() synchronously, and
// this handler calls signIn() as its first statement, so the popup opens in the
// gesture's task. Anything awaited BEFORE signIn() here would spend the gesture and
// Safari would silently swallow the window — which is exactly the failure this
// component exists to recover from, so keep the call first.

import { getIdentity, signIn } from './auth';

export interface SessionExpiredOpts {
  /** Noun phrase for what didn't load, e.g. "the product list". Lower-case. */
  what: string;
  /** What the retry does, for both sentence and button. Defaults to "load". */
  verb?: string;
  /** Extra class on the wrapper so each screen keeps its own visual weight. */
  className?: string;
}

/**
 * Replace `el`'s contents with the sign-in-and-retry prompt.
 *
 * The wording is chosen from the CURRENT identity rather than assumed: an expired
 * session and a never-signed-in one both land here, and telling someone who is
 * simply signed out that their "session expired" is a small lie that makes the app
 * look confused about its own state.
 *
 * `retry` re-runs the caller's action after a successful sign-in. It is only invoked
 * when sign-in actually produced an authenticated identity — a cancelled or
 * popup-blocked attempt leaves the prompt in place so the user can tap again.
 */
export function renderSessionExpired(
  el: HTMLElement,
  opts: SessionExpiredOpts,
  retry: () => void | Promise<void>,
): void {
  const { what, verb = 'load', className = '' } = opts;
  const expired = getIdentity().expired;
  const message = expired
    ? `Your sign-in expired, so ${what} couldn't ${verb}.`
    : `You're signed out, so ${what} couldn't ${verb}.`;
  const label = `Sign in and ${verb}`;

  el.innerHTML = `
    <div class="session-x ${className}">
      <p class="session-x__msg">🔒 ${esc(message)}</p>
      <button type="button" class="btn btn--primary session-x__btn">${esc(label)}</button>
    </div>`;

  const btn = el.querySelector<HTMLButtonElement>('.session-x__btn')!;
  btn.addEventListener('click', async () => {
    // FIRST statement — see the header note about the iOS gesture.
    const signInPromise = signIn();
    btn.disabled = true;
    btn.textContent = 'Signing in…';
    try {
      const id = await signInPromise;
      if (!id.authenticated) {
        // Cancelled at the Microsoft prompt. Put the button back.
        btn.disabled = false;
        btn.textContent = label;
        return;
      }
      await retry();
    } catch (err) {
      console.warn('re-auth failed', err);
      btn.disabled = false;
      btn.textContent = label;
    }
  });
}

function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}
